import { Dispatch, SetStateAction } from 'react';

import { ICalendarEventType, IMatchingAvailabilitiesData, ServiceTypes, TimeFormat } from '@pharmaplan/common';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { DynamicTableCell, nameLabels, OtherScreens, ScreenTypes } from '../../../helpers/Constants';
import addAvailablilityIcon from '../../../assets/svg/addAvailabilityIcon.svg';
import approveBookingIcon from '../../../assets/svg/approveBooking.svg';
import deleteIcon from '../../../assets/svg/delete.svg';
import { renderScreen } from '../../../actions/drawerActions';
import { getDayMonthDateYear } from '../../Reports/helpers';
import { timeString } from '../../../helpers/Functions';
import strings from '../../../localization';
import { store } from '../../../store';
import { dateAndTime } from '../AdminReports/helper';

const isSelfService = ServiceTypes.self === ServiceHandler.getService();

export interface ITableRowConfig {
  data: Array<IMatchingAvailabilitiesData>;
  timeFormat: TimeFormat;
  dispatch: typeof store.dispatch;
  workshiftId: string;
  type: ICalendarEventType;
  back: () => void
  showDeletePreSubmit: (availabilityId: string, dateTime: string) => void;
  canMakeBooking?: boolean
}

export const tableHeaderConfig = () =>
  [
    { key: '0', label: nameLabels().roleLabel },
    { key: '1', label: strings.date },
    { key: '2', label: strings.time },
    { key: '3', label: strings.action },
  ];

export const tableRowConfig = ({
  data,
  timeFormat,
  dispatch,
  workshiftId,
  type,
  back,
  showDeletePreSubmit,
  canMakeBooking,
}: ITableRowConfig) =>
  data?.map((item) => {
    const {
      pharmacistName,
      startDate,
      professionalName,
      endDate,
      availableTimeId,
      selectedDate,
      pharmacistId,
      isRequested,
    } = item ?? {};

    const time = timeString(startDate, endDate, timeFormat);
    const date = getDayMonthDateYear(selectedDate);

    const dateTime = dateAndTime(selectedDate, startDate, endDate, timeFormat);
    const name = isSelfService ? professionalName : pharmacistName;

    let tooltipConfig = {
      icon: addAvailablilityIcon,
      tooltip: strings.book,
    };

    if (isRequested) {
      tooltipConfig = {
        icon: approveBookingIcon,
        tooltip: strings.approve,
      };
    }

    return {
      key: `${availableTimeId}`,
      data: [
        {
          key: `0-${availableTimeId}`,
          value: {
            label: name,
            handlePress: () => {
              dispatch(
                renderScreen({
                  screenNumber: 2,
                  screenType: ScreenTypes.profile,
                  pharmacistId,
                  fromScreen: OtherScreens.MatchingAvailabilities,
                  type,
                  back,
                }),
              );
            },
          },
          type: DynamicTableCell.smallerCustomLink,
        },
        {
          key: `1-${availableTimeId}`,
          value: date,
          type: DynamicTableCell.smallerText,
        },
        {
          key: `2-${availableTimeId}`,
          value: time,
          type: DynamicTableCell.smallerText,
        },
        {
          key: `3-${availableTimeId}`,
          show: true,
          value: [
            {
              key: '1',
              icon: tooltipConfig.icon,
              tooltip: tooltipConfig.tooltip,
              disabled: !canMakeBooking,
              onClick: () => {
                dispatch(
                  renderScreen({
                    screenNumber: 4,
                    screenType: ScreenTypes.requestedList,
                    fromScreen: OtherScreens.MatchingAvailabilities,
                    eventId: availableTimeId,
                    workshiftId,
                    type,
                  }),
                );
              },
            },
            {
              key: '2',
              icon: deleteIcon,
              tooltip: strings.delete,
              onClick: () => {
                showDeletePreSubmit(availableTimeId, dateTime);
              },
            },
          ],
          type: DynamicTableCell.iconButton,
        },
      ],
    };
  });

export const handleClick = (
  setShowOtherAvailabilities: Dispatch<SetStateAction<boolean>>,
  state: boolean,
) =>
  () => {
    setShowOtherAvailabilities(state);
  };
